import React, { useEffect, useState } from 'react';
import { Mail, Download, Send, Plus, Clock, Trash2, Eye } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import toast from 'react-hot-toast';
import { campaignTemplates } from '../../utils/campaignTemplates';

const NewsletterAdminPage: React.FC = () => {
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [activeTab, setActiveTab] = useState<'subscribers' | 'campaigns'>('subscribers');
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<any>(null);
  const [selectedTemplate, setSelectedTemplate] = useState('classic');
  const [campaignForm, setCampaignForm] = useState({
    subject: '',
    heading: '',
    body: '',
    ctaText: '',
    ctaUrl: '',
    scheduleEnabled: false,
    scheduledDate: '',
    scheduledTime: '',
  });
  const [isSending, setIsSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      setIsLoading(true);
      const res = await adminAPI.getSubscribers();
      if (res.success && res.data) setSubscribers(res.data);
    } catch {
      toast.error('Failed to fetch subscribers');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      setIsLoadingCampaigns(true);
      const res = await adminAPI.getCampaigns();
      if (res.success && res.data) setCampaigns(res.data);
    } catch {
      toast.error('Failed to fetch campaigns');
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'campaigns') fetchCampaigns();
  }, [activeTab]);

  const buildHtml = () => {
    const template = campaignTemplates.find((t) => t.id === selectedTemplate);
    if (!template) return '';
    return template.generateHtml({
      heading: campaignForm.heading,
      body: campaignForm.body.replace(/\n/g, '<br>'),
      ctaText: campaignForm.ctaText || undefined,
      ctaUrl: campaignForm.ctaUrl || undefined,
    });
  };

  const handlePreview = () => {
    setPreviewHtml(buildHtml());
    setShowPreview(true);
  };

  const resetComposer = () => {
    setCampaignForm({
      subject: '',
      heading: '',
      body: '',
      ctaText: '',
      ctaUrl: '',
      scheduleEnabled: false,
      scheduledDate: '',
      scheduledTime: '',
    });
    setSelectedTemplate('classic');
    setEditingCampaign(null);
    setShowComposer(false);
  };

  const handleSaveCampaign = async (sendNow: boolean) => {
    if (!campaignForm.subject.trim() || !campaignForm.heading.trim() || !campaignForm.body.trim()) {
      toast.error('Subject, heading, and body are required');
      return;
    }

    try {
      setIsSending(true);
      const contentHtml = buildHtml();

      let scheduledAt: string | undefined;
      if (campaignForm.scheduleEnabled && campaignForm.scheduledDate && campaignForm.scheduledTime) {
        scheduledAt = new Date(`${campaignForm.scheduledDate}T${campaignForm.scheduledTime}`).toISOString();
      }

      const payload = {
        subject: campaignForm.subject,
        template_id: selectedTemplate,
        content_html: contentHtml,
        scheduled_at: scheduledAt,
      };

      let campaignId: string;
      if (editingCampaign) {
        const res = await adminAPI.updateCampaign(editingCampaign.id, payload);
        campaignId = res.data.id;
        toast.success('Campaign updated');
      } else {
        const res = await adminAPI.createCampaign(payload);
        campaignId = res.data.id;
        toast.success('Campaign saved');
      }

      if (sendNow) {
        const res = await adminAPI.sendCampaignNow(campaignId);
        toast.success(`Campaign sent to ${res.data.recipient_count} subscribers`);
      }

      resetComposer();
      fetchCampaigns();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to save campaign');
    } finally {
      setIsSending(false);
    }
  };

  const handleEditCampaign = (campaign: any) => {
    setEditingCampaign(campaign);
    setSelectedTemplate(campaign.template_id);
    setCampaignForm({
      subject: campaign.subject,
      heading: '',
      body: '',
      ctaText: '',
      ctaUrl: '',
      scheduleEnabled: !!campaign.scheduled_at,
      scheduledDate: campaign.scheduled_at ? campaign.scheduled_at.split('T')[0] : '',
      scheduledTime: campaign.scheduled_at ? campaign.scheduled_at.split('T')[1]?.slice(0, 5) : '',
    });
    setShowComposer(true);
  };

  const handleDeleteCampaign = async (id: string) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await adminAPI.deleteCampaign(id);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch {
      toast.error('Failed to delete campaign');
    }
  };

  // A sent campaign can't be edited or resent (matches the backend, which
  // intentionally blocks changes to something already delivered). This
  // opens the composer pre-filled with its subject/template as a new
  // draft instead, so a past campaign can be reused without altering
  // history.
  const handleDuplicateCampaign = (campaign: any) => {
    setEditingCampaign(null);
    setSelectedTemplate(campaign.template_id);
    setCampaignForm({
      subject: campaign.subject,
      heading: '',
      body: '',
      ctaText: '',
      ctaUrl: '',
      scheduleEnabled: false,
      scheduledDate: '',
      scheduledTime: '',
    });
    setShowComposer(true);
    toast('Started a new draft with the same subject and template. Please re-enter your message text — only the final HTML was saved for the original.', { icon: '📋', duration: 5000 });
  };

  const handleSendExisting = async (id: string) => {
    if (!window.confirm('Send this campaign to all active subscribers now?')) return;
    try {
      const res = await adminAPI.sendCampaignNow(id);
      toast.success(`Sent to ${res.data.recipient_count} subscribers`);
      fetchCampaigns();
    } catch {
      toast.error('Failed to send campaign');
    }
  };

  const handleExport = () => {
    const csv = [
      ['Email', 'Subscribed At'],
      ...subscribers.map((s) => [
        s.email,
        new Date(s.created_at).toLocaleDateString(),
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subscribers.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported successfully');
  };

  const filtered = subscribers.filter((s) =>
    s.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-widest uppercase text-nude mb-1">Management</p>
          <h1 className="font-serif text-3xl text-primary">Newsletter</h1>
        </div>
        <button
          onClick={handleExport}
          className="btn-secondary flex items-center space-x-2"
        >
          <Download size={14} />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-8 border-b border-light">
        <button
          onClick={() => setActiveTab('subscribers')}
          className={`pb-3 text-xs tracking-widest uppercase transition-colors ${
            activeTab === 'subscribers'
              ? 'text-primary border-b-2 border-primary'
              : 'text-nude hover:text-primary'
          }`}
        >
          Subscribers
        </button>
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`pb-3 text-xs tracking-widest uppercase transition-colors ${
            activeTab === 'campaigns'
              ? 'text-primary border-b-2 border-primary'
              : 'text-nude hover:text-primary'
          }`}
        >
          Campaigns
        </button>
      </div>

      {activeTab === 'subscribers' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-light p-6">
              <p className="text-xs tracking-widest uppercase text-nude mb-2">Total</p>
              <p className="font-serif text-3xl text-primary">{subscribers.length}</p>
            </div>
            <div className="bg-white border border-light p-6">
              <p className="text-xs tracking-widest uppercase text-nude mb-2">Active</p>
              <p className="font-serif text-3xl text-primary">
                {subscribers.filter((s) => s.is_active).length}
              </p>
            </div>
            <div className="bg-white border border-light p-6">
              <p className="text-xs tracking-widest uppercase text-nude mb-2">This Month</p>
              <p className="font-serif text-3xl text-primary">
                {subscribers.filter((s) => {
                  const date = new Date(s.created_at);
                  const now = new Date();
                  return (
                    date.getMonth() === now.getMonth() &&
                    date.getFullYear() === now.getFullYear()
                  );
                }).length}
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nude" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="SEARCH SUBSCRIBERS"
              className="w-full bg-white border border-light pl-9 pr-4 py-3 text-xs tracking-widest outline-none focus:border-primary transition-colors"
            />
          </div>

          {/* Table */}
          <div className="bg-white border border-light overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-light">
                  {['Email', 'Status', 'Subscribed'].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={3} className="px-6 py-8 text-center">
                      <div className="skeleton h-6 w-48 mx-auto rounded" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-6 py-8 text-center text-xs text-nude tracking-widest uppercase"
                    >
                      No subscribers found
                    </td>
                  </tr>
                ) : (
                  filtered.map((sub) => (
                    <tr
                      key={sub.id}
                      className="border-b border-light hover:bg-light/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-7 h-7 bg-light rounded-full flex items-center justify-center">
                            <Mail size={12} className="text-nude" />
                          </div>
                          <span className="text-xs">{sub.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`text-xs tracking-widest uppercase px-2 py-1 rounded ${
                            sub.is_active
                              ? 'bg-green-50 text-green-600'
                              : 'bg-red-50 text-red-500'
                          }`}
                        >
                          {sub.is_active ? 'Active' : 'Unsubscribed'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-nude">
                        {new Date(sub.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'campaigns' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-xs tracking-widest uppercase text-nude">
              {campaigns.length} Campaign{campaigns.length !== 1 ? 's' : ''}
            </p>
            <button
              onClick={() => setShowComposer(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus size={14} />
              <span>New Campaign</span>
            </button>
          </div>

          {/* Composer */}
          {showComposer && (
            <div className="bg-white border border-light p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-xl text-primary">
                  {editingCampaign ? 'Edit Campaign' : 'Compose Campaign'}
                </h2>
                <button onClick={resetComposer} className="text-xs tracking-widest uppercase text-nude hover:text-primary">
                  Cancel
                </button>
              </div>

              {/* Template Picker */}
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-3">
                  Choose Template
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {campaignTemplates.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTemplate(t.id)}
                      className={`text-left p-4 border transition-colors ${
                        selectedTemplate === t.id
                          ? 'border-primary bg-light'
                          : 'border-light hover:border-nude'
                      }`}
                    >
                      <p className="text-xs font-medium text-primary mb-1">{t.name}</p>
                      <p className="text-xs text-nude leading-relaxed">{t.preview}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Email Subject
                </label>
                <input
                  value={campaignForm.subject}
                  onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
                  placeholder="e.g. The New Collection Has Arrived"
                  className="input-luxury"
                />
              </div>

              {/* Heading */}
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Heading
                </label>
                <input
                  value={campaignForm.heading}
                  onChange={(e) => setCampaignForm({ ...campaignForm, heading: e.target.value })}
                  placeholder="e.g. Introducing Autumn/Winter"
                  className="input-luxury"
                />
              </div>

              {/* Body */}
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Message
                </label>
                <textarea
                  value={campaignForm.body}
                  onChange={(e) => setCampaignForm({ ...campaignForm, body: e.target.value })}
                  rows={6}
                  placeholder="Write your announcement here..."
                  className="w-full border border-light p-3 text-xs outline-none focus:border-primary transition-colors resize-none"
                />
              </div>

              {/* CTA */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                    Button Text (optional)
                  </label>
                  <input
                    value={campaignForm.ctaText}
                    onChange={(e) => setCampaignForm({ ...campaignForm, ctaText: e.target.value })}
                    placeholder="Shop Now"
                    className="input-luxury"
                  />
                </div>
                <div>
                  <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                    Button Link (optional)
                  </label>
                  <input
                    value={campaignForm.ctaUrl}
                    onChange={(e) => setCampaignForm({ ...campaignForm, ctaUrl: e.target.value })}
                    placeholder="https://lanresavage.com/shop"
                    className="input-luxury"
                  />
                </div>
              </div>

              {/* Schedule */}
              <div>
                <label className="flex items-center space-x-2 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={campaignForm.scheduleEnabled}
                    onChange={(e) => setCampaignForm({ ...campaignForm, scheduleEnabled: e.target.checked })}
                    className="accent-primary"
                  />
                  <span className="text-xs tracking-widest uppercase">Schedule for later</span>
                </label>
                {campaignForm.scheduleEnabled && (
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="date"
                      value={campaignForm.scheduledDate}
                      onChange={(e) => setCampaignForm({ ...campaignForm, scheduledDate: e.target.value })}
                      className="input-luxury"
                    />
                    <input
                      type="time"
                      value={campaignForm.scheduledTime}
                      onChange={(e) => setCampaignForm({ ...campaignForm, scheduledTime: e.target.value })}
                      className="input-luxury"
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3 pt-4 border-t border-light">
                <button
                  onClick={handlePreview}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Eye size={14} />
                  <span>Preview</span>
                </button>
                <button
                  onClick={() => handleSaveCampaign(false)}
                  disabled={isSending}
                  className="btn-secondary disabled:opacity-50"
                >
                  Save as Draft
                </button>
                {campaignForm.scheduleEnabled ? (
                  <button
                    onClick={() => handleSaveCampaign(false)}
                    disabled={isSending}
                    className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Clock size={14} />
                    <span>{isSending ? 'Saving...' : 'Schedule'}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => handleSaveCampaign(true)}
                    disabled={isSending}
                    className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Send size={14} />
                    <span>{isSending ? 'Sending...' : 'Send Now'}</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Preview Modal */}
          {showPreview && (
            <div
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6"
              onClick={() => setShowPreview(false)}
            >
              <div
                className="bg-white max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-4 border-b border-light">
                  <p className="text-xs tracking-widest uppercase text-nude">Email Preview</p>
                  <button onClick={() => setShowPreview(false)} className="text-xs tracking-widest uppercase text-nude hover:text-primary">
                    Close
                  </button>
                </div>
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  className="w-full"
                  style={{ height: '70vh', border: 'none' }}
                />
              </div>
            </div>
          )}

          {/* Campaign History */}
          <div className="bg-white border border-light overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-light">
                  {['Subject', 'Template', 'Status', 'Recipients', 'Date', 'Actions'].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoadingCampaigns ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center">
                      <div className="skeleton h-6 w-48 mx-auto rounded" />
                    </td>
                  </tr>
                ) : campaigns.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-xs text-nude tracking-widest uppercase">
                      No campaigns yet
                    </td>
                  </tr>
                ) : (
                  campaigns.map((c) => (
                    <tr key={c.id} className="border-b border-light hover:bg-light/50 transition-colors">
                      <td className="px-6 py-4 text-xs font-medium text-primary">{c.subject}</td>
                      <td className="px-6 py-4 text-xs text-nude">
                        {campaignTemplates.find((t) => t.id === c.template_id)?.name || c.template_id}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`text-xs tracking-widest uppercase px-2 py-1 rounded ${
                          c.status === 'sent'
                            ? 'bg-green-50 text-green-600'
                            : c.status === 'scheduled'
                            ? 'bg-yellow-50 text-yellow-600'
                            : 'bg-light text-nude'
                        }`}>
                          {c.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-nude">{c.recipient_count || '—'}</td>
                      <td className="px-6 py-4 text-xs text-nude">
                        {c.sent_at
                          ? new Date(c.sent_at).toLocaleDateString()
                          : c.scheduled_at
                          ? `Scheduled: ${new Date(c.scheduled_at).toLocaleString()}`
                          : new Date(c.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          {c.status !== 'sent' ? (
                            <>
                              <button onClick={() => handleEditCampaign(c)} className="text-nude hover:text-primary transition-colors text-xs tracking-widest uppercase">
                                Edit
                              </button>
                              <button onClick={() => handleSendExisting(c.id)} className="text-nude hover:text-primary transition-colors">
                                <Send size={14} />
                              </button>
                              <button onClick={() => handleDeleteCampaign(c.id)} className="text-nude hover:text-red-500 transition-colors">
                                <Trash2 size={14} />
                              </button>
                            </>
                          ) : (
                            <button onClick={() => handleDuplicateCampaign(c)} className="text-nude hover:text-primary transition-colors text-xs tracking-widest uppercase whitespace-nowrap">
                              Duplicate as Draft
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterAdminPage;